import { AppRole, CurrentCompanyType, UserType } from "@/interfaces";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * User store state interface
 */
interface SuperAdminSnapshot {
  user: UserType | null;
  token: string | null;
  isAuthenticated: boolean;
  currentCompany?: CurrentCompanyType | null;
}

interface UserState {
  user: UserType | null;
  token: string | null;
  superAdminStore?: SuperAdminSnapshot | null;
  adminStore?: SuperAdminSnapshot | null,
  isAuthenticated: boolean;
  companyId?: string | null;
  currentCompany: CurrentCompanyType | null;
  isLoading: boolean;

  // Actions
  setUser: (user: UserType, token: string) => void;
  updateUser: (user: Partial<UserType>) => void;
  setCurrentCompany: (company: CurrentCompanyType | null) => void;
  logout: () => void;
  getRole: () => AppRole;
  hasCompanies: () => boolean;
  beginImpersonation: (impersonatedUser: UserType, token: string, company?: CurrentCompanyType | null) => void;
  exitImpersonation: () => void;
}

/**
 * User store for authentication and user data management
 * Persists to localStorage for session persistence
 */
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      currentCompany: null,
      isLoading: false,
      superAdminStore: undefined,
      adminStore: undefined,

      beginImpersonation: (impersonatedUser, token, company = null) => {
        const state = get();
        if (state.user?.role !== "Super" && state.user?.role !== "Admin") return;
        const snapshot: SuperAdminSnapshot = {
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          currentCompany: state.user?.role === "Admin" ? state.currentCompany : null,
        };

        if (state.user?.role === "Super") {
          set({
            superAdminStore: snapshot,
            user: impersonatedUser,
            token,
            isAuthenticated: true,
            currentCompany: company || impersonatedUser?.companies?.[0] || null,
          });
        }

        if (state.user?.role === "Admin" && state.superAdminStore) {
          set({
            superAdminStore: state.superAdminStore,
            adminStore: snapshot,
            user: impersonatedUser,
            token,
            isAuthenticated: true,
            currentCompany: company || impersonatedUser?.companies?.[0] || null,
          });
        }

        if (state.user?.role === "Admin" && !state.superAdminStore) {
          set({
            superAdminStore: undefined,
            adminStore: snapshot,
            user: impersonatedUser,
            token,
            isAuthenticated: true,
            currentCompany: company || impersonatedUser?.companies?.[0] || null,
          });
        }

      },

      exitImpersonation: () => {
        const state = get();
        const superAdminSnap = state.superAdminStore;
        const adminSnap = state.adminStore;
        if (!superAdminSnap && !adminSnap) return;

        // Restore Super Admin state and explicitly clear currentCompany
        // Super Admins don't have companies, so always set to null
        if (adminSnap && superAdminSnap) {
          set({
            user: adminSnap.user,
            token: adminSnap.token,
            isAuthenticated: adminSnap.isAuthenticated,
            currentCompany: adminSnap.currentCompany || null,
            superAdminStore: superAdminSnap,
            adminStore: null,
          });
        } else if (superAdminSnap && !adminSnap) {
          set({
            user: superAdminSnap.user,
            token: superAdminSnap.token,
            isAuthenticated: superAdminSnap.isAuthenticated,
            currentCompany: null,
            superAdminStore: null,
          });
        } else if(adminSnap && !superAdminSnap) {
          set({
            user: adminSnap.user,
            token: adminSnap.token,
            isAuthenticated: adminSnap.isAuthenticated,
            currentCompany: adminSnap.currentCompany || null,
            adminStore: undefined,
          });
        }

        // Force update localStorage to ensure currentCompany is cleared
        if (state.user?.role === "Admin") {
          try {
            setTimeout(() => {
              const stored = localStorage.getItem("user-storage");
              if (stored) {
                const parsed = JSON.parse(stored);
                parsed.state.currentCompany = null;
                localStorage.setItem("user-storage", JSON.stringify(parsed));
              }
            }, 1000);
          } catch (error) {
            console.error("Failed to update localStorage:", error);
          }
        }
      },

      /**
       * Sets the authenticated user and token
       * @param user - User object from authentication response
       * @param token - JWT authentication token
       */
      setUser: (user, token) => {
        set({
          user,
          token,
          isAuthenticated: true,
          currentCompany: user?.companies?.[0] || null,
        });
      },

      /**
       * Updates specific fields of the current user
       * @param user - Partial user object with fields to update
       */
      updateUser: (user) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...user } : null,
        })),

      /**
       * Sets the current active company
       * @param company - Company object from user's companies list
       */
      setCurrentCompany: (company) =>
        set({
          currentCompany: company,
        }),

      /**
       * Clears user data and logs out
       * Also clears all Cognito tokens and session storage
       */
      logout: () => {
        // Clear Cognito tokens from localStorage
        localStorage.removeItem("cognito_refresh_token");
        localStorage.removeItem("cognito_access_token");
        localStorage.removeItem("cognito_id_token");

        // Clear any Cognito state from sessionStorage
        sessionStorage.removeItem("cognito_oauth_state");

        // Clear Zustand state
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          currentCompany: null,
          superAdminStore: undefined,
        });
      },

      /**
       * Gets the current user's role
       * @returns User role or 'Unauthenticated' fallback if not authenticated
       */
      getRole: (): AppRole => {
        const state = get();
        if (!state.user?.role) {
          return "Unauthenticated";
        }
        return state.user.role;
      },

      /**
       * Checks if the current user has associated companies
       * @returns true if user has at least one company, false otherwise
       */
      hasCompanies: (): boolean => {
        const state = get();
        return !!(state.user?.companies && state.user.companies.length > 0);
      },
    }),
    {
      name: "user-storage",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        currentCompany: state.currentCompany,
        superAdminStore: state.superAdminStore && {
          user: state.superAdminStore.user,
          token: state.superAdminStore.token,
          isAuthenticated: state.superAdminStore.isAuthenticated,
        },
        adminStore: state.adminStore && {
          user: state.adminStore.user,
          token: state.adminStore.token,
          isAuthenticated: state.adminStore.isAuthenticated,
          currentCompany: state.adminStore.currentCompany,
        },
      }),
    },
  ),
);
